import { useState } from "react";
import type {
  AccountingLocation,
  AccountingMonth,
  AccountingTargets,
  AccountingYear,
  AccountingYearTotal,
} from "@dentacore/shared-types";

import {
  BilledLegend,
  HeadlineTiles,
  OVERHEAD_GROUPS,
  OverheadLegend,
  OverheadRows,
  OwedByAge,
} from "./billing-cards";
import { YearPicker } from "./controls";
import { FlowChart } from "./flow-chart";
import { buildFlow } from "./flow-layout";
import {
  AmountCells,
  FigureTiles,
  signClass,
  sumMonths,
  UnpricedNote,
  WorkLegend,
  workTitle,
} from "./figures";
import { money, monthName, monthShort, parseDay } from "./format";
import { ProfitChart } from "./profit-chart";
import type { ChartBar } from "./profit-chart";
import { StackedChart } from "./stacked-chart";
import type { StackedBar } from "./stacked-chart";
import { useModules } from "../shell/modules-context";
import styles from "./accounting.module.css";

/**
 * Yearly — one year at a glance (STORY-180b, ER-D1, ER-D8).
 *
 * The three figures for the year, then **Expected and realized by month**:
 * Expected (every booking but cancelled and no-show) as a pale bar, Realized
 * (the finished ones) solid inside it. Clicking a month opens it on Monthly.
 * Under that, the cash profit by month and the years side by side, as the
 * old Overview had them.
 *
 * **Every figure comes from the API as it is** and this adds months up. The
 * cash figures of the current year count up to the clinic's today (AC-D17),
 * hence "so far"; Expected deliberately does not stop there — the months ahead
 * show what is already booked into them.
 */
export function YearlyTab({
  data,
  years,
  year,
  yearOptions,
  onYear,
  onMonth,
  currency,
  selectionLabel,
  targets,
  locations,
}: {
  /** Null while the year is loading. */
  data: AccountingYear | null;
  years: readonly AccountingYearTotal[] | null;
  year: number;
  yearOptions: readonly number[];
  onYear: (year: number) => void;
  /** Open a month on the Monthly tab. */
  onMonth: (month: number) => void;
  currency: string;
  selectionLabel: string;
  targets: AccountingTargets | null;
  /** The locations selected — the left side of the money flow (FL-D2). */
  locations: readonly AccountingLocation[];
}): React.JSX.Element {
  // Materials are Supply's: without it Accounting shows none (QF-D4).
  const hasSupply = useModules().has("SUPPLY");
  const [showWork, setShowWork] = useState(false);
  const [showMonths, setShowMonths] = useState(false);

  const controls = (
    <div className={styles.controls}>
      <YearPicker years={yearOptions} value={year} onChange={onYear} testId="accounting-year" />
      <p className={styles.note}>Showing {selectionLabel}.</p>
    </div>
  );

  if (data === null) {
    return (
      <>
        {controls}
        <div className={styles.card}>
          <p className={styles.blank}>Loading…</p>
        </div>
      </>
    );
  }

  const today = parseDay(data.today);
  const isCurrentYear = data.year === today.year;
  const soFar = isCurrentYear ? " so far" : "";
  const total = sumMonths(data.months);
  const started = data.months.some((month) => month.started);

  /* ── billed and collected, and where the money goes, by month ─────────── */

  const billedBars: ChartBar[] = data.months.map((row) => ({
    id: String(row.month),
    label: monthShort(row.month),
    value: row.started ? row.moneyIn : null,
    ghost: row.started ? row.billed : null,
    title: row.started
      ? `${monthName(row.month)} ${String(data.year)}: billed ${money(row.billed, currency)}, collected ${money(row.moneyIn, currency)}`
      : `${monthName(row.month)} ${String(data.year)} has not started`,
  }));

  const overheadBars: StackedBar[] = data.months.map((row) => ({
    id: String(row.month),
    label: monthShort(row.month),
    segments: OVERHEAD_GROUPS.map((group) => row.overhead[group.key]),
    title: `${monthName(row.month)} ${String(data.year)}: ${OVERHEAD_GROUPS.map((group) => `${group.label.toLowerCase()} ${money(row.overhead[group.key], currency)}`).join(", ")}`,
  }));

  /* ── expected and realized, by month ──────────────────────────────────── */

  const workBars: ChartBar[] = data.months.map((row) => ({
    id: String(row.month),
    label: monthShort(row.month),
    value: row.finished.profitCents,
    ghost: row.booked.profitCents,
    title: `${monthName(row.month)} ${String(data.year)}: ${workTitle("expected", row.booked, currency, hasSupply)}; ${workTitle("realized", row.finished, currency, hasSupply)}`,
  }));

  /* ── cash profit, by month ────────────────────────────────────────────── */

  const monthBars: ChartBar[] = data.months.map((row) => ({
    id: String(row.month),
    label: monthShort(row.month),
    value: row.started ? row.profit : null,
    title: row.started
      ? `${monthName(row.month)} ${String(data.year)}: profit ${money(row.profit, currency)}${row.current ? " (so far)" : ""}`
      : `${monthName(row.month)} ${String(data.year)} has not started`,
  }));

  /* ── where the money went ─────────────────────────────────────────────── */

  const flow = buildFlow({
    locations,
    byLocation: data.months.reduce<Record<string, number>>((sum, row) => {
      for (const [id, cents] of Object.entries(row.byLocation)) sum[id] = (sum[id] ?? 0) + cents;
      return sum;
    }, {}),
    moneyIn: total.moneyIn,
    overhead: total.overhead,
    profit: total.profit,
  });

  /* ── by year ──────────────────────────────────────────────────────────── */

  const yearRows = yearOptions.map((option) => {
    const found = (years ?? []).find((row) => row.year === option);
    return {
      year: option,
      current: option === today.year,
      profit: found?.profit ?? 0,
      expected: found?.expected ?? 0,
      realized: found?.realized ?? 0,
    };
  });

  const yearBars: ChartBar[] = yearRows.map((row) => ({
    id: String(row.year),
    label: row.current ? `${String(row.year)} to date` : String(row.year),
    value: row.profit,
    selected: row.year === data.year,
    title: `${String(row.year)}${row.current ? " so far" : ""}: profit ${money(row.profit, currency)}`,
  }));

  return (
    <>
      {controls}

      <HeadlineTiles
        totals={total}
        owed={data.owed}
        started={started}
        currency={currency}
        targets={targets}
      />

      <FigureTiles
        period={`${String(data.year)}${soFar}`}
        totals={total}
        profitStarted={started}
        currency={currency}
      />

      <section className={styles.card} data-testid="accounting-billed-by-month">
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Billed and collected, {data.year}</h2>
          <span className={styles.spacer} />
          <BilledLegend />
        </div>
        <div className={styles.cardBody}>
          <ProfitChart
            bars={billedBars}
            currency={currency}
            label={`Billed and collected by month, ${String(data.year)}`}
            onPick={(id) => onMonth(Number(id))}
            height={170}
            showValues={false}
            testId="accounting-billed-month-chart"
          />
          <p className={styles.note}>
            Billed is invoices issued; collected is payments received. A payment can settle an
            earlier month&rsquo;s bill, so one month can collect more than it billed. Click a month
            to open it.
          </p>
        </div>
      </section>

      <div className={styles.pairGrid}>
        <section className={styles.card} data-testid="accounting-owed-card">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Owed by age</h2>
          </div>
          <div className={styles.cardBody}>
            <OwedByAge owed={data.owed} currency={currency} />
          </div>
        </section>

        <section className={styles.card} data-testid="accounting-overhead-card">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Where the money goes, {data.year}</h2>
            <span className={styles.spacer} />
            <OverheadLegend />
          </div>
          <div className={styles.cardBody}>
            <StackedChart
              bars={overheadBars}
              segmentClasses={OVERHEAD_GROUPS.map((group) => group.className)}
              currency={currency}
              label={`Money out by group by month, ${String(data.year)}`}
              onPick={(id) => onMonth(Number(id))}
              height={170}
              testId="accounting-overhead-chart"
            />
            <OverheadRows groups={total.overhead} moneyIn={total.moneyIn} currency={currency} />
          </div>
        </section>
      </div>

      <section className={styles.card} data-testid="accounting-work-by-month">
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Expected and realized, {data.year}</h2>
          <span className={styles.spacer} />
          <WorkLegend />
        </div>
        <div className={styles.cardBody}>
          <ProfitChart
            bars={workBars}
            currency={currency}
            label={`Expected and realized revenue by month, ${String(data.year)}`}
            onPick={(id) => onMonth(Number(id))}
            height={180}
            showValues={false}
            testId="accounting-work-month-chart"
          />
          <p className={styles.note}>
            Expected is the profit of every booking but cancelled ones and no-shows; realized is the
            finished ones.{" "}
            {hasSupply ? "Each is price minus materials." : "Each is the bookings' price."} Click a
            month to open it.
          </p>
          <UnpricedNote count={total.unpriced} />
          <button
            type="button"
            className={`${styles.quiet} ${styles.toggle}`}
            onClick={() => setShowWork((was) => !was)}
            aria-expanded={showWork}
            data-testid="accounting-work-toggle"
          >
            {showWork ? "Hide the numbers" : "Show the numbers"}
          </button>
          {showWork && (
            <div className={styles.tableWrap}>
              <table className={styles.table} data-testid="accounting-work-table">
                <thead>
                  <tr>
                    <th />
                    <th className={styles.right}>Bookings</th>
                    <th className={styles.right}>Expected</th>
                    <th className={styles.right}>Finished</th>
                    <th className={styles.right}>Realized</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map((row) => (
                    <WorkRow
                      key={row.month}
                      label={monthName(row.month)}
                      month={row}
                      currency={currency}
                    />
                  ))}
                  <WorkRow label={String(data.year)} month={total} currency={currency} totalRow />
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className={styles.card} data-testid="accounting-flow">
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>
            Money in to profit, {data.year}
            {soFar}
          </h2>
        </div>
        <div className={styles.cardBody}>
          <FlowChart
            flow={flow}
            currency={currency}
            label={`Where the money went, ${String(data.year)}: locations into money in, then staff, supplies and lab, occupancy, other and profit`}
            testId="accounting-flow-chart"
          />
          <p className={styles.note}>
            Each block is sized by its money. A year that costs more than it took in shows a
            shortfall instead of profit.
          </p>
        </div>
      </section>

      <div className={styles.overviewGrid}>
        <section className={styles.card} data-testid="accounting-by-year">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>By year</h2>
          </div>
          <div className={styles.cardBody}>
            <ProfitChart
              bars={yearBars}
              currency={currency}
              label="Profit by year"
              onPick={(id) => onYear(Number(id))}
              height={180}
              testId="accounting-year-chart"
            />
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th />
                    <th className={styles.right}>Profit</th>
                    <th className={styles.right}>Expected</th>
                    <th className={styles.right}>Realized</th>
                  </tr>
                </thead>
                <tbody>
                  {yearRows.map((row) => (
                    <tr
                      key={row.year}
                      className={row.year === data.year ? styles.selectedRow : undefined}
                    >
                      <td>
                        {row.year}
                        {row.current ? " to date" : ""}
                      </td>
                      <td className={`${styles.right} ${signClass(row.profit) ?? ""}`}>
                        {money(row.profit, currency)}
                      </td>
                      <td className={styles.right}>{money(row.expected, currency)}</td>
                      <td className={styles.right}>{money(row.realized, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className={styles.card} data-testid="accounting-by-month">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Cash profit by month, {data.year}</h2>
            <span className={styles.spacer} />
            <div className={styles.legend} aria-hidden="true">
              <span>
                <i className={`${styles.swatch} ${styles.swatchProfit}`} />
                Profit
              </span>
              <span>
                <i className={`${styles.swatch} ${styles.swatchLoss}`} />
                Loss
              </span>
            </div>
          </div>
          <div className={styles.cardBody}>
            <ProfitChart
              bars={monthBars}
              currency={currency}
              label={`Profit by month, ${String(data.year)}`}
              onPick={(id) => onMonth(Number(id))}
              height={180}
              testId="accounting-month-chart"
            />
            <p className={styles.note}>
              {hasSupply
                ? "Payments received minus materials, salaries and expenses."
                : "Payments received minus salaries and expenses."}
              {isCurrentYear &&
                ` ${monthName(today.month)} counts up to today, ${String(today.day)} ${monthName(today.month)}.`}
            </p>
            <button
              type="button"
              className={`${styles.quiet} ${styles.toggle}`}
              onClick={() => setShowMonths((was) => !was)}
              aria-expanded={showMonths}
              data-testid="accounting-months-toggle"
            >
              {showMonths ? "Hide the numbers" : "Show the numbers"}
            </button>
            {showMonths && (
              <div className={styles.tableWrap}>
                <table className={styles.table} data-testid="accounting-month-table">
                  <thead>
                    <tr>
                      <th />
                      <th className={styles.right}>Money in</th>
                      {hasSupply && <th className={styles.right}>Materials</th>}
                      <th className={styles.right}>Salaries</th>
                      <th className={styles.right}>Expenses</th>
                      <th className={styles.right}>Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.months.map((row) =>
                      row.started ? (
                        <tr key={row.month}>
                          <td>
                            {monthName(row.month)}
                            {row.current ? " (so far)" : ""}
                          </td>
                          <AmountCells totals={row} currency={currency} materials={hasSupply} />
                        </tr>
                      ) : (
                        <tr key={row.month} className={styles.dimRow}>
                          <td>{monthName(row.month)}</td>
                          <td className={styles.right} colSpan={hasSupply ? 5 : 4}>
                            Not started
                          </td>
                        </tr>
                      ),
                    )}
                    <tr className={styles.totalRow}>
                      <td>
                        {data.year}
                        {soFar}
                      </td>
                      <AmountCells totals={total} currency={currency} materials={hasSupply} />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

/** One line of the expected-and-realized numbers. */
function WorkRow({
  label,
  month,
  currency,
  totalRow = false,
}: {
  label: string;
  month: Pick<AccountingMonth, "booked" | "finished">;
  currency: string;
  totalRow?: boolean;
}): React.JSX.Element {
  return (
    <tr className={totalRow ? styles.totalRow : undefined}>
      <td>{label}</td>
      <td className={styles.right}>{month.booked.count}</td>
      <td className={`${styles.right} ${signClass(month.booked.profitCents) ?? ""}`}>
        {money(month.booked.profitCents, currency)}
      </td>
      <td className={styles.right}>{month.finished.count}</td>
      <td className={`${styles.right} ${signClass(month.finished.profitCents) ?? ""}`}>
        {money(month.finished.profitCents, currency)}
      </td>
    </tr>
  );
}
